/**
 * ArchivedExpensesView — Dossier "Archives" visible dans l'onglet Achats (Admin).
 *
 * Liste tous les achats archivés via la "Remise à zéro" Admin
 * (flag `archived=true`). Permet de :
 *   - Consulter chaque ligne d'achat archivé avec métadonnées
 *     (qui a archivé, quand, statut au moment de l'archivage)
 *   - Restaurer un ou plusieurs achats individuellement
 *   - Voir les totaux groupés par mois
 *
 * Source des données : GET /api/expenses?only_archived=true
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Archive, RotateCcw, RefreshCw, Loader2, FileText, ShieldAlert, Printer, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatPrice = (p) => new Intl.NumberFormat("fr-FR").format(p || 0);
const fmtDate = (iso) => { try { return format(parseISO(iso), "dd/MM/yyyy", { locale: fr }); } catch { return iso || "—"; } };
const fmtDateTime = (iso) => { try { return format(parseISO(iso), "dd/MM/yyyy HH:mm", { locale: fr }); } catch { return iso || "—"; } };

// Ouvre un onglet imprimable avec un ensemble d'achats archivés (PDF via l'impression navigateur)
function printArchivedExpenses(list, { title = "Achats archivés", subtitle = "" } = {}) {
  if (!list || list.length === 0) return;
  const total = list.reduce((s, e) => s + (e.amount || 0), 0);
  const rows = list.map((e, idx) => {
    const items = (e.items || []).length > 0
      ? `<ul style="margin:4px 0 0 12px;padding:0;font-size:10.5px;color:#475569">${
          e.items.map(it => `<li>${it.description || "—"} — ${it.quantity || 1} × ${formatPrice(it.unit_price || 0)} F = <b>${formatPrice(it.amount || (it.quantity || 1) * (it.unit_price || 0))} F</b></li>`).join("")
        }</ul>`
      : "";
    return `
      <tr style="border-bottom:1px solid #e2e8f0">
        <td style="padding:6px 8px;font-size:11px;vertical-align:top">${idx + 1}</td>
        <td style="padding:6px 8px;font-size:11px;vertical-align:top">${fmtDate(e.created_at)}</td>
        <td style="padding:6px 8px;font-size:11px;vertical-align:top">
          <b>${e.description || "—"}</b>
          <div style="color:#64748b;font-size:10px">
            ${e.category || ""}${e.supplier ? " · " + e.supplier : ""}${e.status ? " · " + e.status : ""}
          </div>
          ${items}
        </td>
        <td style="padding:6px 8px;font-size:11px;vertical-align:top;text-align:right;font-family:monospace;color:#065f46;font-weight:700">
          ${formatPrice(e.amount || 0)} F
        </td>
        <td style="padding:6px 8px;font-size:10px;vertical-align:top;color:#64748b">
          ${e.archived_at ? fmtDateTime(e.archived_at) : "—"}<br/>
          ${e.archived_by ? "par " + e.archived_by : ""}
        </td>
      </tr>`;
  }).join("");
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${title}</title>
<style>
  @page { size: A4; margin: 12mm; }
  body { font-family: 'Segoe UI', Arial, sans-serif; color:#0f172a; margin:0; }
  h1 { font-size:20px; margin:0 0 4px; letter-spacing:-0.02em; }
  .sub { color:#64748b; font-size:11px; margin-bottom:8px; }
  .meta { display:flex; justify-content:space-between; align-items:center; padding:8px 0; border-bottom:2px solid #0f172a; margin-bottom:8px; }
  table { width:100%; border-collapse:collapse; margin-top:6px; }
  thead td { background:#1e293b; color:white; font-size:10px; text-transform:uppercase; letter-spacing:0.1em; padding:6px 8px; font-weight:700; }
  tfoot td { background:#f1f5f9; font-size:13px; font-weight:800; padding:8px; border-top:2px solid #0f172a; }
</style></head><body>
  <div class="meta">
    <div>
      <h1>ESPACE MAXO — ${title}</h1>
      <div class="sub">${subtitle} · Édité le ${new Date().toLocaleString("fr-FR")}</div>
    </div>
    <div style="text-align:right;font-size:11px;color:#64748b">
      ${list.length} achat${list.length > 1 ? "s" : ""}<br/>
      <b style="color:#0f172a;font-size:14px;font-family:monospace">${formatPrice(total)} F CFA</b>
    </div>
  </div>
  <table>
    <thead>
      <tr>
        <td style="width:30px">#</td>
        <td style="width:80px">Date</td>
        <td>Description / Articles</td>
        <td style="width:100px;text-align:right">Montant</td>
        <td style="width:120px">Archivé</td>
      </tr>
    </thead>
    <tbody>${rows}</tbody>
    <tfoot>
      <tr>
        <td colspan="3" style="text-align:right">TOTAL</td>
        <td style="text-align:right;font-family:monospace">${formatPrice(total)} F</td>
        <td></td>
      </tr>
    </tfoot>
  </table>
  <p style="margin-top:16px;font-size:9px;color:#94a3b8;text-align:center">
    Document généré depuis Caisse Pro · Archives Achats · Espace Maxo — Fijrosse, Cotonou
  </p>
</body></html>`;
  const w = window.open("", "_blank");
  if (!w) { toast.error("Le navigateur a bloqué l'ouverture — autorisez les pop-ups"); return; }
  w.document.write(html);
  w.document.close();
  setTimeout(() => { w.focus(); w.print(); }, 400);
}

const ArchivedExpensesView = ({ currentUser }) => {
  const isAdmin = currentUser?.role === "admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [restoring, setRestoring] = useState(false);
  // 🆕 Filtre : "all" | "month_end" | "manual_reset" | "manual"
  const [reasonFilter, setReasonFilter] = useState("all");

  const fetchArchived = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/expenses`, {
        params: { only_archived: true },
      });
      const list = Array.isArray(r.data) ? r.data : r.data.expenses || [];
      list.sort((a, b) =>
        String(b.archived_at || b.created_at || "").localeCompare(
          String(a.archived_at || a.created_at || "")
        )
      );
      setItems(list);
    } catch (e) {
      toast.error("Erreur chargement des archives");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) fetchArchived();
  }, [fetchArchived, isAdmin]);

  // Refresh en direct quand un archivage se déclenche ailleurs (auto, manuel…)
  useEffect(() => {
    if (!isAdmin) return;
    const handler = () => fetchArchived();
    window.addEventListener("expenses-archived", handler);
    window.addEventListener("purchases-archived", handler);
    return () => {
      window.removeEventListener("expenses-archived", handler);
      window.removeEventListener("purchases-archived", handler);
    };
  }, [fetchArchived, isAdmin]);

  // Compteurs par raison
  const reasonCounts = React.useMemo(() => {
    const c = { all: items.length, month_end: 0, manual_reset: 0, manual: 0, other: 0 };
    items.forEach((e) => {
      const r = e.archive_reason || "other";
      if (r in c) c[r]++;
      else c.other++;
    });
    return c;
  }, [items]);

  const filteredItems = React.useMemo(() => {
    if (reasonFilter === "all") return items;
    return items.filter((e) => (e.archive_reason || "other") === reasonFilter);
  }, [items, reasonFilter]);

  if (!isAdmin) {
    return (
      <Card className="bg-rose-950/30 border-rose-500/40">
        <CardContent className="py-8 text-center text-rose-300 text-sm">
          <ShieldAlert className="w-8 h-8 mx-auto mb-2" />
          Dossier réservé à l'administrateur.
        </CardContent>
      </Card>
    );
  }

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (selected.size === filteredItems.length) setSelected(new Set());
    else setSelected(new Set(filteredItems.map((e) => e.id)));
  };

  const restoreSelected = async () => {
    if (selected.size === 0) return;
    setRestoring(true);
    try {
      const r = await axios.post(`${API}/admin/maintenance/restore-one`, {
        ids: Array.from(selected),
        actor_role: "admin",
        actor_name: currentUser?.full_name || currentUser?.username || "Admin",
      });
      toast.success(`${r.data.restored} achat(s) restauré(s)`);
      setSelected(new Set());
      fetchArchived();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la restauration");
    } finally {
      setRestoring(false);
    }
  };

  // Groupement par mois d'archivage (yyyy-MM) — sur la liste filtrée
  const groups = filteredItems.reduce((acc, e) => {
    const d = e.archived_at || e.created_at || "";
    const key = d ? d.slice(0, 7) : "—";
    if (!acc[key]) acc[key] = { month: key, items: [], total: 0 };
    acc[key].items.push(e);
    acc[key].total += e.amount || 0;
    return acc;
  }, {});
  const groupKeys = Object.keys(groups).sort((a, b) => b.localeCompare(a));

  const grandTotal = filteredItems.reduce((s, e) => s + (e.amount || 0), 0);

  const REASON_LABELS = {
    month_end: { label: "Mois terminé", cls: "bg-cyan-500/20 text-cyan-300 border border-cyan-500/40" },
    manual_reset: { label: "Remise à zéro", cls: "bg-rose-500/20 text-rose-300 border border-rose-500/40" },
    manual: { label: "Archivé manuellement", cls: "bg-amber-500/20 text-amber-300 border border-amber-500/40" },
    other: { label: "Sans motif", cls: "bg-slate-600/30 text-slate-300 border border-slate-600" },
  };

  return (
    <div className="space-y-3" data-testid="archived-expenses-view">
      {/* Header / summary */}
      <Card className="bg-gradient-to-br from-slate-900/60 to-slate-800/40 border-slate-700">
        <CardHeader className="pb-2">
          <CardTitle className="text-slate-200 flex items-center justify-between gap-2 flex-wrap text-base">
            <div className="flex items-center gap-2">
              <Archive className="w-5 h-5 text-amber-300" />
              Dossier Archives — Achats
              <Badge className="bg-amber-500/20 text-amber-300 border border-amber-500/40 text-[10px]">
                {filteredItems.length} ligne{filteredItems.length > 1 ? "s" : ""}
                {reasonFilter !== "all" && ` / ${items.length}`}
              </Badge>
              <Badge className="bg-slate-700/60 text-slate-200 text-[10px]">
                Total : {formatPrice(grandTotal)} F
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={fetchArchived}
                disabled={loading}
                className="border-slate-600 text-slate-300"
                data-testid="archived-refresh-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                Actualiser
              </Button>
              <Button
                size="sm"
                onClick={toggleAll}
                variant="outline"
                disabled={filteredItems.length === 0}
                className="border-slate-600 text-slate-300"
                data-testid="archived-toggle-all-btn"
              >
                {selected.size === filteredItems.length && filteredItems.length > 0 ? "Tout décocher" : "Tout cocher"}
              </Button>
              {/* 🆕 PDF Global (tous les archives visibles) */}
              <Button
                size="sm"
                onClick={() => printArchivedExpenses(filteredItems, { title: "Achats archivés — Ensemble", subtitle: `${filteredItems.length} achat(s) · ${formatPrice(grandTotal)} F` })}
                disabled={filteredItems.length === 0}
                className="bg-sky-600 hover:bg-sky-700"
                data-testid="archived-print-all-btn"
                title="Exporter la liste filtrée en PDF"
              >
                <Download className="w-4 h-4 mr-1" /> PDF Global
              </Button>
              {/* 🆕 PDF Sélection */}
              <Button
                size="sm"
                onClick={() => {
                  const list = filteredItems.filter(e => selected.has(e.id));
                  printArchivedExpenses(list, { title: "Achats archivés — Sélection", subtitle: `${list.length} achat(s) sélectionné(s)` });
                }}
                disabled={selected.size === 0}
                className="bg-violet-600 hover:bg-violet-700"
                data-testid="archived-print-selection-btn"
                title="Exporter la sélection en PDF"
              >
                <Printer className="w-4 h-4 mr-1" /> PDF Sélection ({selected.size})
              </Button>
              <Button
                size="sm"
                onClick={restoreSelected}
                disabled={selected.size === 0 || restoring}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="archived-restore-btn"
              >
                {restoring ? (
                  <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Restauration…</>
                ) : (
                  <><RotateCcw className="w-4 h-4 mr-1" /> Restaurer la sélection ({selected.size})</>
                )}
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="text-[11px] text-slate-400 pt-2 border-t border-slate-700/50 space-y-2">
          <div>
            Ces achats ont été archivés pour isoler les recettes du mois en cours.
            Ils n&apos;apparaissent plus dans les totaux (statistiques, compte courant, dashboard) mais
            restent ici pour consultation et restauration éventuelle.
          </div>
          {/* 🆕 Filtres par raison d'archivage */}
          <div className="flex flex-wrap gap-1.5 pt-1">
            {[
              { key: "all", label: "Tous", cls: "bg-slate-700/60 text-slate-200 border border-slate-500" },
              { key: "month_end", label: "Mois terminé", cls: "bg-cyan-500/10 text-cyan-300 border border-cyan-500/40" },
              { key: "manual", label: "Archivé manuellement", cls: "bg-amber-500/10 text-amber-300 border border-amber-500/40" },
              { key: "manual_reset", label: "Remise à zéro", cls: "bg-rose-500/10 text-rose-300 border border-rose-500/40" },
            ].map((f) => {
              const count = reasonCounts[f.key] || 0;
              if (f.key !== "all" && count === 0) return null;
              const active = reasonFilter === f.key;
              return (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setReasonFilter(f.key)}
                  className={`text-[11px] px-2.5 py-1 rounded-full transition-all ${f.cls} ${
                    active ? "ring-2 ring-white/40 scale-105" : "opacity-70 hover:opacity-100"
                  }`}
                  data-testid={`archived-reason-filter-${f.key}`}
                >
                  {f.label} <span className="ml-1 font-mono">({count})</span>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Listing groupé par mois */}
      {filteredItems.length === 0 ? (
        <Card className="bg-slate-800/30 border-slate-700">
          <CardContent className="py-12 text-center">
            <Archive className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-500">
              {reasonFilter === "all"
                ? "Aucun achat archivé pour l'instant."
                : "Aucun achat archivé avec ce motif."}
            </p>
            <p className="text-slate-600 text-xs mt-1">
              Les achats des mois terminés sont archivés automatiquement à l&apos;ouverture de l&apos;onglet Achats.
            </p>
          </CardContent>
        </Card>
      ) : (
        groupKeys.map((key) => {
          const g = groups[key];
          return (
            <Card key={key} className="bg-slate-800/30 border-slate-700">
              <CardHeader className="pb-1">
                <CardTitle className="text-slate-200 text-sm flex items-center justify-between gap-2">
                  <span className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-slate-400" />
                    Archivé{g.items.length > 1 ? "s" : ""} en {key === "—" ? "période inconnue" : (
                      (() => {
                        try { return format(parseISO(key + "-01"), "MMMM yyyy", { locale: fr }); }
                        catch { return key; }
                      })()
                    )}
                    <Badge className="text-[10px] bg-slate-700/60 text-slate-200">
                      {g.items.length}
                    </Badge>
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="text-amber-300 text-xs font-bold">
                      {formatPrice(g.total)} F
                    </span>
                    {/* 🆕 PDF du mois */}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        const label = key === "—" ? "période inconnue" : (() => {
                          try { return format(parseISO(key + "-01"), "MMMM yyyy", { locale: fr }); }
                          catch { return key; }
                        })();
                        printArchivedExpenses(g.items, {
                          title: `Achats archivés — ${label}`,
                          subtitle: `${g.items.length} achat(s) · ${formatPrice(g.total)} F`,
                        });
                      }}
                      className="h-7 px-2 border-sky-500/50 text-sky-300 hover:bg-sky-500/20"
                      title="Exporter ce mois en PDF"
                      data-testid={`archived-print-month-${key}`}
                    >
                      <Printer className="w-3 h-3 mr-1" /> PDF du mois
                    </Button>
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1.5">
                {g.items.map((e) => (
                  <div
                    key={e.id}
                    className={`flex items-center gap-2 p-2 rounded border ${
                      selected.has(e.id)
                        ? "bg-emerald-900/20 border-emerald-500/50"
                        : "bg-slate-900/40 border-slate-700/50"
                    }`}
                    data-testid={`archived-expense-${e.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(e.id)}
                      onChange={() => toggle(e.id)}
                      className="w-4 h-4 accent-emerald-500 cursor-pointer flex-shrink-0"
                      data-testid={`archived-select-${e.id}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-white text-sm font-medium truncate">
                          {e.description || "—"}
                        </span>
                        {e.category && (
                          <Badge className="text-[10px] bg-slate-700/60 text-slate-300">
                            {e.category}
                          </Badge>
                        )}
                        {e.status && (
                          <Badge className="text-[10px] bg-amber-500/20 text-amber-300">
                            {e.status}
                          </Badge>
                        )}
                        {(() => {
                          const r = e.archive_reason || "other";
                          const meta = REASON_LABELS[r] || REASON_LABELS.other;
                          return (
                            <Badge className={`text-[10px] ${meta.cls}`}>
                              {meta.label}
                            </Badge>
                          );
                        })()}
                      </div>
                      <div className="text-[10px] text-slate-400 mt-0.5 flex flex-wrap gap-x-3">
                        {e.created_at && (
                          <span>
                            Créé le {format(parseISO(e.created_at), "dd/MM/yyyy", { locale: fr })}
                          </span>
                        )}
                        {e.archived_at && (
                          <span>
                            Archivé le {format(parseISO(e.archived_at), "dd/MM/yyyy HH:mm", { locale: fr })}
                            {e.archived_by ? ` par ${e.archived_by}` : ""}
                          </span>
                        )}
                        {e.requested_by && (
                          <span>Demandé par {e.requested_by}</span>
                        )}
                      </div>
                    </div>
                    <span className="text-amber-300 font-bold text-sm whitespace-nowrap">
                      {formatPrice(e.amount)} F
                    </span>
                    {/* 🆕 PDF individuel */}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => printArchivedExpenses([e], {
                        title: `Achat archivé — ${e.description || "—"}`,
                        subtitle: fmtDate(e.created_at),
                      })}
                      className="h-7 w-7 p-0 text-sky-400 hover:bg-sky-500/20 flex-shrink-0"
                      title="Exporter cet achat en PDF"
                      data-testid={`archived-print-single-${e.id}`}
                    >
                      <Printer className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          );
        })
      )}
    </div>
  );
};

export default ArchivedExpensesView;
